import { Injectable, signal } from '@angular/core';
import View from 'ol/View';
import * as olExtent from 'ol/extent';
import { MapService } from './map.service';
import proj4 from 'proj4';
import * as olProj from 'ol/proj';
import { easeIn } from 'ol/easing';

import { register } from 'ol/proj/proj4';
import { Observable, BehaviorSubject, fromEventPattern } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Extent } from 'ol/extent';

@Injectable({
  providedIn: 'root'
})
export class ViewService {

  private view: View;
  private viewReadySubject = new BehaviorSubject<boolean>(false);
  public viewReady$: Observable<boolean>;

  readonly resolution = signal<number | undefined>(undefined);

  private searchUrl: string;

  constructor(
    private mapService: MapService,
    private http: HttpClient,
  ) {
    this.viewReady$ = this.viewReadySubject.asObservable();
  }

  initView(zoomLevel: number, center: number[], projectionCode: string, extent: Extent, minZoom: number, maxZoom: number) {
    this.initCustomProjections();
    //case lamb 2088, default is webmercator
    if (projectionCode === 'EPSG:3812') {
      // let projectionExtent = [517579.6608, 523633.8376, 797118.5854, 745328.5980];
      let projectionExtent = [484647.1136, 502357.8792, 822367.8263, 780354.4553];
      let projection = olProj.get('EPSG:3812');
      projection.setExtent(projectionExtent);
      const viewResolutions = [
        1058.3333333333333, // 0
        529.1666666666666, // 1
        211.66666666666666,// 2
        132.29166666666666,// 3
        66.14583333333333,// 4
        26.458333333333332,// 5
        13.229166666666666,// 6
        6.614583333333333,// 7
        2.6458333333333335,// 8
        1.3229166666666667,// 9
        0.6614583333333334, // 10
        0.330729828126323,
        0.1653649140631615
      ];
      // let viewResolutions = [
      //   1058.3354500042335,
      //   529.1677250021168,
      //   211.667090000847,
      //   132.2919312505292,
      //   66.1459656252646,
      //   26.458386250105836,
      //   13.229193125052918,
      //   6.614596562526459,
      //   2.6458386250105836,
      //   1.3229193125052918,
      //   0.661459656252646,
      //   0.330729828126323,
      //   0.1653649140631615
      // ];
      this.view = new View({
        projection: projection,
        resolutions: viewResolutions,
        center: center,
        zoom: zoomLevel,
        // constrainResolution: true,
        // smoothResolutionConstraint: false,

        constrainResolution: true,
        smoothResolutionConstraint: true,
        smoothExtentConstraint: false,
        constrainOnlyCenter: true,
        extent: extent,
        //rotation:15
        //enableRotation: false
      });
      this.mapService.getMap().setView(this.view);
    }
    else {
      // this.view = this.mapService.getMap().getView();
      // this.setZoom(zoomLevel);
      // this.setCenter(center);
      // this.view.setConstrainResolution(true);


      this.view = new View({
        center: center,
        zoom: zoomLevel,
        constrainResolution: true,
        smoothResolutionConstraint: true,
        smoothExtentConstraint: false,
        constrainOnlyCenter: true,
        extent: extent,
        minZoom: minZoom,
        maxZoom: maxZoom
        //rotation:15
        //enableRotation: false
      });
      this.mapService.getMap().setView(this.view);


    }
    this.setSearchUrl();
    this.setViewReady(true);
    this.hookResolutionListener();
  }

  getView() {
    return this.view;
  }

  setViewReady(value: boolean) {
    this.viewReadySubject.next(value);
  }

  getZoom(): number {
    return this.view.getZoom();
  }

  getCenter() {
    return this.view.getCenter();
  }

  getResolution() {
    return this.view.getResolution();
  }

  getProjection() {
    return this.view.getProjection();
  }

  setZoom(zoomLevel: number) {
    this.view.setZoom(zoomLevel);
  }

  setCenter(coord: number[]) {
    this.view.setCenter(coord);
  }
  setCenterAnimate(coord: number[], onAnimateEndFunction?) {
    this.view.animate(
      {
        center: coord,
        easing: easeIn,
      }
      , (onAnimateEndFunction) ? onAnimateEndFunction : ''
    );
  }

  pan(x: number, y: number) {
    const ZOOM: number = 17;
    //always the same timing, with or without zoom
    this.view.animate({
      center: [x, y],
      duration: ZOOM === this.view.getZoom() ? 2000 : 2000 / 2,
    }, ZOOM !== this.view.getZoom() ? {
      duration: 2000 / 2,
      zoom: ZOOM
    } : {}
    );
  }

  zoomAndPan(x: number, y: number, zoomLevel: number, onAnimateEndFunction?) {//: Function   
    const ZOOM: number = zoomLevel || 17;
    //always the same timing, with or without zoom
    this.view.animate({
      center: [x, y],
      duration: ZOOM === this.view.getZoom() ? 750 : 750 / 2,
    },
      ZOOM !== this.view.getZoom() ? {
        duration: 750 / 2,
        zoom: ZOOM
      } : {}
      , (onAnimateEndFunction) ? onAnimateEndFunction : ''
    )
  }

  zoomToFeatureExtent(geom, padding?: number[], onAnimateEndFunction?) {
    let ext = geom.getExtent();
    this.view.fit(ext,
      {
        size: this.mapService.getMap().getSize(),
        padding: padding ? padding : [0, 0, 0, 0],
        duration: 500,
        maxZoom: this.view.getProjection().getCode() === 'EPSG:3812' ? 11 : 18,
        callback: (onAnimateEndFunction) ? onAnimateEndFunction : ''
      });
  };

  flyToFeatureExtent(extent: Extent, padding?: number, onAnimateEndFunction?) {
    let ext = olExtent.buffer(extent, padding);
    let resolution = this.view.getResolutionForExtent(ext);
    let zoom = Math.floor(this.view.getZoomForResolution(resolution));
    let oldZoom = this.view.getZoom();
    let flightZoom = Math.min(oldZoom, zoom) - 1;
    let zoomUp = oldZoom - flightZoom;
    let zoomDown = zoom - flightZoom;
    let center = olExtent.getCenter(ext);
    let duration = 1500;
    this.view.animate({
      center: center,
      duration: duration / 2
    });
    this.view.animate({
      zoom: flightZoom,
      duration: duration * zoomUp / (zoomUp + zoomDown)
    }, {
      zoom: zoom,
      duration: duration * zoomDown / (zoomUp + zoomDown)
    },
      (onAnimateEndFunction) ? onAnimateEndFunction : ''
    );
  };

  setSearchUrl() {
    if (this.getProjection().getCode() === 'EPSG:3812') {
      this.searchUrl = 'https://geocodelist.ngi.be/api/?f=full&l=8&epsg=3812&q='
    } else {
      this.searchUrl = 'https://geocodelist.ngi.be/api/?f=full&l=8&epsg=3857&q='
    }
  }

  search(val: string): Observable<any> {
    return this.http.get<any>(this.searchUrl + val)
      .pipe(map(res => res.features));
  }

  public initCustomProjections(): void {
    //Lambert 1972
    proj4.defs("EPSG:31370", "+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.868628,52.297783,-103.723893,0.336570,-0.456955,1.842183,-1.2747 +units=m +no_defs");
    //Lambert 2008
    proj4.defs("EPSG:3812", "+title=Lamb08 +proj=lcc +lat_1=49.833333333333336 +lat_2=51.16666666666667 +lat_0=50.79781500000001 +lon_0=4.359215833333335 +x_0=649328.0 +y_0=665262.0 +ellps=GRS80 +towgs84=0.0,0.0,0.0,0.0,0.0,0.0,0.0 +units=m +no_defs");

    // add here other proj def
    // ...

    //ETRS89
    proj4.defs("EPSG:4258", "+proj=longlat +ellps=GRS80 +no_defs");
    //utm zones 25->39
    for (let i = 25; i < 39; i++) {
      proj4.defs("EPSG:326" + i, "+proj=utm +zone=" + i + " +datum=WGS84 +units=m +no_defs");
    }


    //register projections
    register(proj4);

    //Lambert 2008 extent
    let projectionExtent = [484647.1136336568, 502357.87922870886, 822367.8262509668, 780354.4553065482];
    olProj.get('EPSG:3812').setExtent(projectionExtent);
    olProj.get('EPSG:3812').setWorldExtent(projectionExtent);


    // var source = new proj4.Proj('EPSG:4258');  
    // var dest = new proj4.Proj('EPSG:3812');      
    // // $("#lat").val(4970142.88145653);
    // // $("#lng").val(500532.52879695);
    // // $("#convert").on("click", function(){
    //     var p = new proj4.Point(-5.63367890602, 54.7456830692);
    //     proj4.transform(source, dest, p);
    //     // console.log("X : " +p.x + " \nY : " + p.y);
    //     // alert("X : " +p.x + " \nY : " + p.y);
    //     proj4(source,dest,[-5.63367890602, 54.7456830692]);

    //       console.log(proj4(source,dest,[-5.63367890602, 54.7456830692]))
    //       console.log(proj4(source,dest,[14.3740682877, 46.7301966169]))

  }

  private hookResolutionListener(): void {

    this.resolution.set(
      this.view.getResolution()
    );

    const resolution$ = fromEventPattern(
      handler => this.view.on('change:resolution', handler)
    );

    resolution$
      .pipe(
        debounceTime(200)
      )
      .subscribe(() => {

        const resolution =
          this.view.getResolution();
        this.resolution.set(resolution);
      });
  }
}
